<template>
  <div class="relative pt-24 menu-page">
    <CleanupReservations />

    <!-- Cart Button (FAB) -->
    <div class="fixed top-40 z-40" :class="isArabic ? 'left-4' : 'right-4'">
      <button @click="showCart = true" class="fab">
        <img src="@/assets/icons/cart.svg" alt="Cart" class="w-6 h-6" />
        <span v-if="cartItemCount > 0" class="fab-badge">
          {{ cartItemCount }}
        </span>
      </button>
    </div>

    <!-- Background Image -->
<div class="absolute inset-0 -z-10">
  <img
    src="@/assets/Menu/ourMenu.png"
    alt="Hero Background"
    class="absolute inset-0 w-full h-[400px] sm:h-[500px] md:h-[400px] object-cover object-bottom"
  />
  <!-- Hero Overlay (dark only via theme var) -->
  <div class="absolute inset-0 menu-hero-overlay pointer-events-none"></div>
</div>


    <!--  Hero Section -->
    <section class="text-center px-6 py-20 max-w-4xl mx-auto">
      <h1 class="font-['DM_Serif_Display'] text-2xl sm:text-3xl md:text-3xl lg:text-4xl menu-hero-title mb-4">
        {{ $t("menu.title") }}
      </h1>
      <p class="font-['DM_Sans'] text-sm sm:text-base md:text-lg lg:text-xl font-light menu-hero-subtitle">
        {{ $t("menu.subtitle") }}
      </p>
    </section>

    <!-- Appetizer Section -->
    <section class="menu-section px-4 py-12 pb-24">
      <div class="max-w-5xl mx-auto">
        <div class="menu-divider">
          <h2 class="font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
            {{ $t("menu.appetizer") }}
          </h2>
        </div>

        <div class="space-y-8">
          <div
            v-for="dish in appetizer"
            :key="dish.id"
            class="menu-item"
          >
            <button
              @click="addToCart(dish)"
              class="fab-mini"
              title="Add to Cart"
            >+</button>

            <img :src="dish.image" alt="Dish" class="w-20 h-20 md:w-20 md:h-20 rounded-md object-cover" />

            <div class="flex flex-col justify-between flex-grow">
              <div>
                <h3 class="item-title font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
                  {{ dish.name }}
                </h3>
                <p class="item-desc font-['DM_Sans'] text-xs sm:text-sm md:text-base lg:text-lg">
                  {{ dish.description }}
                </p>
              </div>
              <p
                :class="[
                  'item-price font-[\'DM_Sans\'] text-base sm:text-lg md:text-xl lg:text-2xl font-normal',
                  isArabic ? 'text-left' : 'text-right',
                ]"
              >
                {{ $t('menu.currency', { amount: dish.price.toFixed(2) }) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Homestyle Sushi Header Image -->
<div class="image-divider relative w-full h-[300px] sm:h-[200px] lg:h-[400px] overflow-hidden menu-section">
  <img
    src="@/assets/Menu/Homestyle-Sushi.png"
    alt="Homestyle Sushi Background"
    class="w-full h-full object-cover object-bottom block"
  />
</div>

    <!-- Homestyle Sushi List Section -->
    <section class="menu-section px-4 py-12 pb-24">
      <div class="max-w-5xl mx-auto">
        <div class="menu-divider">
          <h2 class="font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
            {{ $t("menu.homestyleSushi") }}
          </h2>
        </div>

        <div class="space-y-8">
          <div v-for="sushi in sushiItems" :key="sushi.id" class="menu-item">
            <button @click="addToCart(sushi)" class="fab-mini" title="Add to Cart">+</button>
            <img :src="sushi.image" alt="Sushi" class="w-20 h-20 md:w-20 md:h-20 rounded-md object-cover" />
            <div class="flex flex-col justify-between flex-grow">
              <div>
                <h3 class="item-title font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
                  {{ sushi.name }}
                </h3>
                <p class="item-desc font-['DM_Sans'] text-xs sm:text-sm md:text-base lg:text-lg">
                  {{ sushi.description }}
                </p>
              </div>
              <p :class="['item-price font-[\'DM_Sans\'] text-base sm:text-lg md:text-xl lg:text-2xl font-normal', isArabic ? 'text-left' : 'text-right']">
                {{ $t('menu.currency', { amount: sushi.price.toFixed(2) }) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Steak Header -->
<div class="image-divider relative h-[300px] sm:h-[200px] lg:h-[400px] overflow-hidden">
  <img 
    src="@/assets/Menu/Steak.png" 
    alt="Steak Background" 
    class="w-full h-full object-cover object-bottom" 
  />
</div>


    <!-- Steak Items Section -->
    <section class="menu-section px-4 py-12 pb-24">
      <div class="max-w-5xl mx-auto">
        <div class="menu-divider">
          <h2 class="font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
            {{ $t("menu.steak") }}
          </h2>
        </div>

        <div class="space-y-8">
          <div v-for="steak in steakItems" :key="steak.id" class="menu-item">
            <button @click="addToCart(steak)" class="fab-mini" title="Add to Cart">+</button>
            <img :src="steak.image" alt="Steak" class="w-20 h-20 md:w-20 md:h-20 rounded-md object-cover" />
            <div class="flex flex-col justify-between flex-grow">
              <div>
                <h3 class="item-title font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
                  {{ steak.name }}
                </h3>
                <p class="item-desc font-['DM_Sans'] text-xs sm:text-sm md:text-base lg:text-lg">
                  {{ steak.description }}
                </p>
              </div>
              <p :class="['item-price font-[\'DM_Sans\'] text-base sm:text-lg md:text-xl lg:text-2xl font-normal', isArabic ? 'text-left' : 'text-right']">
                {{ $t('menu.currency', { amount: steak.price.toFixed(2) }) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Chicken Header -->
<div class="image-divider relative h-[300px] sm:h-[200px] lg:h-[400px] overflow-hidden">
  <img 
    src="@/assets/Menu/Chicken.png" 
    alt="Chicken Background" 
    class="w-full h-full object-cover object-bottom" 
  />
</div>

    <!-- Chicken Items Section -->
    <section class="menu-section px-4 py-12 pb-24">
      <div class="max-w-5xl mx-auto">
        <div class="menu-divider">
          <h2 class="font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
            {{ $t("menu.chicken") }}
          </h2>
        </div>

        <div class="space-y-8">
          <div v-for="chicken in chickenItems" :key="chicken.id" class="menu-item">
            <button @click="addToCart(chicken)" class="fab-mini" title="Add to Cart">+</button>
            <img :src="chicken.image" alt="Chicken Dish" class="w-20 h-20 md:w-20 md:h-20 rounded-md object-cover" />
            <div class="flex flex-col justify-between flex-grow">
              <div>
                <h3 class="item-title font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
                  {{ chicken.name }}
                </h3>
                <p class="item-desc font-['DM_Sans'] text-xs sm:text-sm md:text-base lg:text-lg">
                  {{ chicken.description }}
                </p>
              </div>
              <p :class="['item-price font-[\'DM_Sans\'] text-base sm:text-lg md:text-xl lg:text-2xl font-normal', isArabic ? 'text-left' : 'text-right']">
                {{ $t('menu.currency', { amount: chicken.price.toFixed(2) }) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

   <!-- Beverages Header -->
<div class="image-divider relative h-[300px] sm:h-[200px] lg:h-[400px] overflow-hidden">
  <img 
    src="@/assets/Menu/Beverages.png" 
    alt="Beverages Background" 
    class="w-full h-full object-cover object-bottom" 
  />
</div>

    <!-- Beverages Items Section -->
    <section class="menu-section px-4 py-12 pb-24">
      <div class="max-w-5xl mx-auto">
        <div class="menu-divider">
          <h2 class="font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
            {{ $t("menu.beverages") }}
          </h2>
        </div>

        <div class="space-y-8">
          <div v-for="beverage in beverageItems" :key="beverage.id" class="menu-item">
            <button @click="addToCart(beverage)" class="fab-mini" title="Add to Cart">+</button>
            <img :src="beverage.image" alt="Beverage" class="w-20 h-20 md:w-20 md:h-20 rounded-md object-cover" />
            <div class="flex flex-col justify-between flex-grow">
              <div>
                <h3 class="item-title font-['DM_Sans'] text-base sm:text-lg md:text-xl lg:text-2xl">
                  {{ beverage.name }}
                </h3>
                <p class="item-desc font-['DM_Sans'] text-xs sm:text-sm md:text-base lg:text-lg">
                  {{ beverage.description }}
                </p>
              </div>
              <p :class="['item-price font-[\'DM_Sans\'] text-base sm:text-lg md:text-xl lg:text-2xl font-normal', isArabic ? 'text-left' : 'text-right']">
                {{ $t('menu.currency', { amount: beverage.price.toFixed(2) }) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <CartSummary v-if="showCart" :cart="cart" :isFromPreorder="isFromPreorder"
                 @close="showCart = false" @update-cart="updateCart" />
    <SmartChatbot />
  </div>
</template>

<style scoped>
.menu-page { color: var(--on-surface);  }
.menu-hero-overlay {
  background: var(--menu-hero-overlay);
}


/* Sections and headers */
.menu-section { background: var(--bg); color: var(--on-surface); }
.hero-overlay { background: var(--menu-hero-overlay); }

/* Hero */
.menu-hero-title { color: var(--menu-title); }
.menu-hero-subtitle { color: var(--menu-subtitle); }

/* Reusable image divider overlay */
.image-divider { position: relative; }
.image-divider::after {
  content: "";
  position: absolute;
  inset: 0;
  background: var(--image-divider-overlay);
  pointer-events: none;
}


/* Divider bar */
.menu-divider {
  width: 100%;
  background: var(--menu-divider-bg);
  color: var(--menu-divider-text);
  text-align: center;
  padding: .5rem 0; /* py-2 */
  margin-bottom: 2.5rem; /* mb-10 */
  border: 1px solid var(--menu-divider-border);
}

/* Item rows */
.menu-item {
  display: flex;
  align-items: center;
  gap: 1rem;           /* gap-4 */
  background: var(--bg);
  padding: 1rem;       /* p-4 */
  transition: border-color .15s ease;
  border: 1px solid transparent;
}
.menu-item:hover { border-color: var(--menu-item-hover-border); }

.item-title { color: var(--menu-dish-name); font-weight: 600; }
.item-desc { color: var(--menu-desc); }
.item-price { color: var(--menu-price); }

/* FAB (cart) */
.fab {
  background: var(--menu-fab-bg);
  color: var(--menu-fab-icon);
  padding: .75rem;
  border-radius: 9999px;
  box-shadow: 0 10px 15px rgba(0,0,0,.15);
  transition: background .15s ease, transform .1s ease;
  position: relative;
}
.fab:hover { background: var(--menu-fab-hover-bg); transform: translateY(-1px); }

.fab-badge {
  position: absolute; top: -0.5rem; right: -0.5rem;
  background: var(--menu-badge-bg);
  color: var(--menu-badge-text);
  font-size: .75rem;
  width: 1.5rem; height: 1.5rem;
  border-radius: 9999px;
  display: flex; align-items: center; justify-content: center;
}

/* Small + button */
.fab-mini {
  background: var(--menu-fab-bg);
  color: var(--menu-fab-icon);
  border-radius: 9999px;
  width: 2rem; height: 2rem;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.25rem; font-weight: 700;
  transition: background .15s ease;
  flex-shrink: 0;
}
.fab-mini:hover { background: var(--menu-fab-hover-bg); }
</style>


<script>
import CleanupReservations from "@/components/CleanupReservations.vue";
import CartSummary from "@/components/CartSummary.vue";
import SmartChatbot from "@/components/SmartChatbot.vue";

export default {
  name: "MenuPage",
  components: {
    CleanupReservations,
    CartSummary,
    SmartChatbot,
  },
  data() {
    return {
      cart: [], // Cart array
      showCart: false, // Control cart modal visibility
    };
  },
  computed: {
    appetizer() {
      return [
        {
          id: 1,
          name: this.$t("menu.items.tomatoToast.name"),
          description: this.$t("menu.items.tomatoToast.description"),
          price: 120, // LE
          image: require("@/assets/Menu/appetizer/TomatoToast.png"),
        },
        {
          id: 2,
          name: this.$t("menu.items.noodleSoup.name"),
          description: this.$t("menu.items.noodleSoup.description"),
          price: 90, // LE
          image: require("@/assets/Menu/appetizer/NoodleSoup.png"),
        },
        {
          id: 3,
          name: this.$t("menu.items.pumpkinSoup.name"),
          description: this.$t("menu.items.pumpkinSoup.description"),
          price: 90, // LE
          image: require("@/assets/Menu/appetizer/PumpkinSoup.png"),
        },
      ];
    },

    sushiItems() {
      return [
        {
          id: 1,
          name: this.$t("menu.items.dragonSushi.name"),
          description: this.$t("menu.items.dragonSushi.description"),
          price: 480, // LE
          image: require("@/assets/Menu/sushi/Dragon-Sushi.png"),
        },
        {
          id: 2,
          name: this.$t("menu.items.creamySushi.name"),
          description: this.$t("menu.items.creamySushi.description"),
          price: 250, // LE
          image: require("@/assets/Menu/sushi/Creamy-Sushi.png"),
        },
        {
          id: 3,
          name: this.$t("menu.items.rollSalmonSushi.name"),
          description: this.$t("menu.items.rollSalmonSushi.description"),
          price: 360, // LE
          image: require("@/assets/Menu/sushi/Roll-Salmon-Sushi.png"),
        },
      ];
    },
    steakItems() {
      return [
        {
          id: 1,
          name: this.$t("menu.items.steakTartare.name"),
          description: this.$t("menu.items.steakTartare.description"),
          price: 850.0, // LE
          image: require("@/assets/Menu/steak/Steak-tartare.png"),
        },
        {
          id: 2,
          name: this.$t("menu.items.surfTurf.name"),
          description: this.$t("menu.items.surfTurf.description"),
          price: 700.0, // LE
          image: require("@/assets/Menu/steak/SurfTurf.png"),
        },
        {
          id: 3,
          name: this.$t("menu.items.wagyuBeef.name"),
          description: this.$t("menu.items.wagyuBeef.description"),
          price: 950.0, // LE
          image: require("@/assets/Menu/steak/WagyuBeef.png"),
        },
      ];
    },
    chickenItems() {
      return [
        {
          id: 1,
          name: this.$t("menu.items.creamyParmesan.name"),
          description: this.$t("menu.items.creamyParmesan.description"),
          price: 260.0, // LE
          image: require("@/assets/Menu/chicken/Creamy-Parmesan.png"),
        },
        {
          id: 2,
          name: this.$t("menu.items.springChicken.name"),
          description: this.$t("menu.items.springChicken.description"),
          price: 300.0, // LE
          image: require("@/assets/Menu/chicken/Spring-Chicken.png"),
        },
        {
          id: 3,
          name: this.$t("menu.items.chickenFrancese.name"),
          description: this.$t("menu.items.chickenFrancese.description"),
          price: 400.0, // LE
          image: require("@/assets/Menu/chicken/Chicken-Francese.png"),
        },
      ];
    },
    beverageItems() {
      return [
        {
          id: 1,
          name: this.$t("menu.items.panthouseTonic.name"),
          description: this.$t("menu.items.panthouseTonic.description"),
          price: 70.0, // LE
          image: require("@/assets/Menu/beverages/Panthouse-Tonic.png"),
        },
        {
          id: 2,
          name: this.$t("menu.items.appleBreeze.name"),
          description: this.$t("menu.items.appleBreeze.description"),
          price: 70.0, // LE
          image: require("@/assets/Menu/beverages/Apple-Breeze.png"),
        },
        {
          id: 3,
          name: this.$t("menu.items.frenchmanBlitz.name"),
          description: this.$t("menu.items.frenchmanBlitz.description"),
          price: 70.0, // LE
          image: require("@/assets/Menu/beverages/Frenchman-Blitz.png"),
        },
      ];
    },
    cartItemCount() {
      return this.cart.reduce((sum, item) => sum + item.quantity, 0);
    },
    cartTotal() {
      return this.cart.reduce(
        (sum, item) => sum + item.price * (item.quantity || 1),
        0
      );
    },
    isArabic() {
      return this.$i18n && this.$i18n.locale === "ar";
    },
    isFromPreorder() {
      return localStorage.getItem("reservationId") !== null;
    },
  },
  methods: {
    addToCart(dish) {
      const existing = this.cart.find((item) => item.name === dish.name);
      if (existing) {
        existing.quantity += 1;
      } else {
        this.cart.push({
          name: dish.name,
          price: dish.price,
          quantity: 1,
          specialRequest: "",
          showSpecialRequest: false,
        });
      }
      this.saveCartToStorage();
    },
    updateCart(newCart) {
      this.cart = newCart;
      this.saveCartToStorage();
    },
    saveCartToStorage() {
      localStorage.setItem("cart", JSON.stringify(this.cart));
    },
    goToCheckout() {
      this.$router.push({ name: "checkout" });
    },
  },
  mounted() {
    const storedCart = localStorage.getItem("cart");
    if (storedCart) {
      const parsedCart = JSON.parse(storedCart);
      // Ensure all cart items have the new structure
      this.cart = parsedCart.map((item) => ({
        ...item,
        specialRequest: item.specialRequest || "",
        showSpecialRequest: item.showSpecialRequest || false,
      }));
    } else {
      this.cart = [];
    }
  },
  watch: {
    // Watch for language changes and update cart item names
    '$i18n.locale': {
      handler(newLocale, oldLocale) {
        if (newLocale !== oldLocale && this.cart.length > 0) {
          // Update cart item names to match the new language
          this.cart.forEach(item => {
            // Find the dish in the current language's menu items
            const allDishes = [
              ...this.appetizer,
              ...this.sushiItems,
              ...this.steakItems,
              ...this.chickenItems,
              ...this.beverageItems
            ];
            
            // Try to find a matching dish by price (since names change with language)
            const matchingDish = allDishes.find(dish => dish.price === item.price);
            if (matchingDish) {
              item.name = matchingDish.name;
            }
          });
          
          // Save updated cart to localStorage
          this.saveCartToStorage();
        }
      },
      immediate: false
    }
  },
};
</script>
